import { Fragment } from 'react';
import { NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { visibleNav, MODALITY, surfaceForPath, type Modality } from './nav';

// ThemeToggle removed (console-hide-dark-mode-toggle, owner call 2026-07-30):
// console dark mode is half-implemented, so the control offered a broken
// surface. The theme is pinned to light in main.tsx — see the note there
// before restoring this; removing the toggle alone is NOT sufficient.

function ModChip({ mod }: { mod?: Modality }) {
  if (!mod) return null;
  const m = MODALITY[mod];
  return <span className={`mod ${m.cls}`} title={m.title}>{m.label}</span>;
}

// Route-derived breadcrumb: group › surface › [route tail]. The tail is the
// slice past the surface's `to` (e.g. a biomarker id), rendered mono.
function Breadcrumb() {
  const { pathname } = useLocation();
  const surface = surfaceForPath(pathname);
  if (!surface) return null;
  const { group, item } = surface;
  const base = item.to.replace(/\/$/, '');
  const tail = pathname.startsWith(base) ? pathname.slice(base.length).split('/').filter(Boolean) : [];
  const surfaceIsLast = tail.length === 0;
  return (
    <>
      <span className="crumb">{group.title}</span>
      <span className="sep">›</span>
      {surfaceIsLast
        ? <span className="crumb last">{item.label}</span>
        : <Link to={item.to} className="crumb">{item.label}</Link>}
      {tail.map((seg, i) => (
        <Fragment key={i}>
          <span className="sep">›</span>
          <span className="crumb last mono">{decodeURIComponent(seg)}</span>
        </Fragment>
      ))}
    </>
  );
}

export function ConsoleLayout() {
  const { role, signOut } = useAuth();
  const groups = visibleNav(role ?? '');
  return (
    <div className="bwui">
      <div className="topbar">
        <span className="mark">Bioweave Console</span>
        <span className="spacer" />
        <span className="whoami">
          <span>{role}</span>
          <button className="signout" data-testid="signout-btn" onClick={signOut}>Sign out</button>
        </span>
      </div>
      <div className="app">
        <nav className="side" aria-label="Console navigation">
          {groups.map((g) => (
            <div className="grp" key={g.title}>
              <div className="grp-h">
                {g.color ? <span className={`dot ${g.color}`} /> : null}
                <span>{g.title}</span>
                {g.sub ? <span className="sub">· {g.sub}</span> : null}
              </div>
              <div className="nav">
                {g.items.map((i) => (
                  <NavLink key={i.to} to={i.to} end={i.end}
                    className={({ isActive }) => (isActive ? 'active' : undefined)}>
                    <span className="lbl"><span>{i.label}</span></span>
                    <ModChip mod={i.mod} />
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="main">
          <div className="crumbbar" aria-label="Breadcrumb"><Breadcrumb /></div>
          <div className="content"><Outlet /></div>
        </div>
      </div>
    </div>
  );
}
